import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import MatchH2H from "./MatchH2H";
import MatchOdds from "./MatchOdds";
import MatchStandings from "./MatchStandings";
import MatchPredictions from "./MatchPredictions";
import Layout from "../components/Layout";
import {
  FaSpinner,
  FaClock,
  FaPlay,
  FaCheck,
  FaCalendarAlt,
} from "react-icons/fa";

export default function MatchDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [matchDetails, setMatchDetails] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [matchDetailsTab, setMatchDetailsTab] = useState("details");
  const [leagueStandings, setLeagueStandings] = useState([]);
  const [matchH2HData, setMatchH2HData] = useState(null);
  const [matchOddsData, setMatchOddsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasOdds, setHasOdds] = useState(false);

  const getCorrectTimeInfo = (dateString) => {
    if (!dateString) return { localTime: "-", matchDate: null };
    try {
      let normalized = dateString.replace(" ", "T");
      if (!normalized.endsWith("Z") && !/[\+\-]\d\d:?\d\d$/.test(normalized)) {
        normalized += "Z";
      }
      const utcDate = new Date(normalized);
      const localTime = utcDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return { localTime, matchDate: utcDate, display: localTime };
    } catch (err) {
      console.error(err);
      return { localTime: "-", matchDate: null };
    }
  };

  const isMatchPassed = (match) => {
    if (!match || (!match.date && !match.match_date)) return false;
    const matchDate = new Date(match.date || match.match_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    matchDate.setHours(0, 0, 0, 0);
    return matchDate < today;
  };

  const getMatchStatus = (match) => {
    const now = new Date();
    const matchDateStr = match.date || match.match_date;
    if (!matchDateStr) {
      return {
        type: "upcoming",
        label: "Time TBD",
        icon: <FaClock className="inline mr-1" />,
        color: "bg-gray-800/40 text-gray-300 border border-gray-700/50",
        badgeColor: "bg-gray-800/60 text-gray-300",
      };
    }

    try {
      const utcDate = new Date(matchDateStr);
      const matchDateTime = utcDate;
      const timeDifference = matchDateTime.getTime() - now.getTime();
      const minutesDifference = Math.floor(timeDifference / (1000 * 60));

      const formatTimeUntil = (mins) => {
        if (mins < 60) return `In ${mins}min`;
        const hours = Math.floor(mins / 60);
        const remaining = mins % 60;
        return remaining === 0 ? `In ${hours}h` : `In ${hours}h ${remaining}m`;
      };

      if (minutesDifference > 0) {
        return {
          type: "upcoming",
          label: formatTimeUntil(minutesDifference),
          icon: <FaClock className="inline mr-1" />,
          color: "bg-blue-900/40 text-blue-300 border border-blue-700/50",
          badgeColor: "bg-blue-900/60 text-blue-300",
        };
      }

      if (minutesDifference >= -115 && minutesDifference <= 0) {
        return {
          type: "live",
          label: "LIVE",
          icon: <FaPlay className="inline mr-1 animate-pulse" />,
          color: "bg-green-900/40 text-green-300 border border-green-700/50",
          badgeColor: "bg-green-900/60 text-green-300 animate-pulse",
        };
      }

      return {
        type: "finished",
        label: "Finished",
        icon: <FaCheck className="inline mr-1" />,
        color: "bg-red-900/40 text-red-300 border border-red-700/50",
        badgeColor: "bg-red-900/60 text-red-300",
      };
    } catch (err) {
      console.error("Error calculating match status:", err);
      return {
        type: "upcoming",
        label: "Error",
        icon: <FaClock className="inline mr-1" />,
        color: "bg-gray-800/40 text-gray-300 border border-gray-700/50",
        badgeColor: "bg-gray-800/60 text-gray-300",
      };
    }
  };

  const fetchMatchResult = async (matchId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/match/${matchId}/result`,
        {
          headers: { key: process.env.REACT_APP_ACCESKEY },
        },
      );

      if (res.data.hasResult) {
        setMatchResult(res.data);
      }
    } catch (err) {
      console.error("Error fetching match result:", err);
    }
  };

  const fetchMatchDetails = async (matchId) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/match/${matchId}`,
        {
          headers: { key: process.env.REACT_APP_ACCESKEY },
        },
      );
      setMatchDetails(res.data.match || null);

      const status = getMatchStatus(res.data.match);
      if (status.type === "finished") {
        await fetchMatchResult(matchId);
      }

      await Promise.all([
        fetchMatchOdds(matchId),
        fetchMatchH2H(matchId),
        fetchLeagueStandings(res.data.match?.leagueId || matchId),
      ]);
    } catch (err) {
      console.error("Error fetching match details:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchH2H = async (matchId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/h2h/get/${matchId}`,
        {
          headers: { key: process.env.REACT_APP_ACCESKEY },
        },
      );
      setMatchH2HData(res.data || null);
    } catch (err) {
      console.error("Error fetching H2H:", err);
    }
  };

  const fetchMatchOdds = async (fixtureId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/odds/get?fixtureId=${fixtureId}&bookmakerId=8`,
        { headers: { key: process.env.REACT_APP_ACCESKEY } },
      );
      setMatchOddsData(res.data || null);
      const hasValidOdds =
        res.data?.response?.[0]?.bookmakers?.[0]?.bets?.length > 0;
      setHasOdds(hasValidOdds);
    } catch (err) {
      console.error("Error fetching odds:", err);
      setHasOdds(false);
    }
  };

  const fetchLeagueStandings = async (leagueId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/standings/get?leagueId=${leagueId}&season=2025`,
        { headers: { key: process.env.REACT_APP_ACCESKEY } },
      );
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data.response ?? res.data ?? []);
      setLeagueStandings(data);
    } catch (err) {
      console.error("Error fetching standings:", err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchMatchDetails(id);
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />
          <p className="text-gray-400">Loading match details...</p>
        </div>
      </div>
    );
  }

  if (!matchDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-300 text-lg">Match not found</p>
          <div className="absolute top-4 right-4">
            <button
              onClick={() => navigate("/matches")}
              className="px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm md:text-base font-medium transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.5)] hover:scale-105 active:scale-95"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const m = matchDetails;
  const status = getMatchStatus(m);
  const timeInfo = getCorrectTimeInfo(m.date || m.match_date);

  const getCenterContent = () => {
    if (status.type === "finished" && matchResult && matchResult.result) {
      return (
        <div className="text-center space-y-2">
          <div className="text-5xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
            {matchResult.result}
          </div>
        </div>
      );
    }

    return (
      <div className="text-center space-y-2">
        <div className="text-3xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
          VS
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 p-3 md:p-6">
        <div className="max-w-6xl mx-auto bg-gray-900/60 p-4 md:p-6 rounded-xl border border-green-300 shadow-xl">
          {/* Header cu butonul Back responsive */}
          <div className="relative mb-4 md:mb-6">
            {/* Butonul Back - responsive */}
            <div className="absolute top-0 right-0">
              <button
                onClick={() => navigate("/matches")}
                className="px-2 py-1 md:px-4 md:py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs md:text-sm font-medium transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.5)] hover:scale-105 active:scale-95 whitespace-nowrap"
              >
                <span className="hidden sm:inline">Back to Matches</span>
                <span className="sm:hidden">Back</span>
              </button>
            </div>

            {/* Titlu și informații */}
            <div className="pr-16 md:pr-32">
              <h2 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent truncate">
                {m.homeTeam ?? m.home_team} vs {m.awayTeam ?? m.away_team}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-gray-400 text-xs md:text-sm truncate">
                  {m.league ?? m.league_name} • {m.country}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1 md:mt-2 text-xs md:text-sm text-gray-300">
                <div className="flex items-center gap-1">
                  <FaClock className="text-green-400 text-xs md:text-sm" />
                  <span>{timeInfo.display}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FaCalendarAlt className="text-green-400 text-xs md:text-sm" />
                  <span>
                    {m.date ? new Date(m.date).toLocaleDateString() : "-"}
                  </span>
                </div>
                <div
                  className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full inline-flex items-center gap-1 text-xs md:text-sm ${status.color}`}
                >
                  {status.icon}
                  <span>{status.label}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs - responsive, toate pe aceeași linie */}
          <div className="flex flex-nowrap gap-1 md:gap-2 mb-4 md:mb-6 overflow-x-auto pb-1 scrollbar-thin scrollbar-track-gray-800/30 scrollbar-thumb-green-500">
            <button
              className={`px-2 md:px-4 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                matchDetailsTab === "details"
                  ? "bg-green-600 text-white shadow-[0_0_10px_rgba(34,197,94,0.3)]"
                  : "bg-gray-800/40 text-gray-400 hover:text-white"
              }`}
              onClick={() => setMatchDetailsTab("details")}
            >
              Details
            </button>
            <button
              className={`px-2 md:px-4 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                matchDetailsTab === "h2h"
                  ? "bg-green-600 text-white shadow-[0_0_10px_rgba(34,197,94,0.3)]"
                  : "bg-gray-800/40 text-gray-400 hover:text-white"
              }`}
              onClick={async () => {
                await fetchMatchH2H(id);
                setMatchDetailsTab("h2h");
              }}
            >
              H2H
            </button>
            <button
              className={`px-2 md:px-4 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                matchDetailsTab === "standings"
                  ? "bg-green-600 text-white shadow-[0_0_10px_rgba(34,197,94,0.3)]"
                  : "bg-gray-800/40 text-gray-300 hover:text-white"
              }`}
              onClick={async () => {
                await fetchLeagueStandings(m.leagueId || id);
                setMatchDetailsTab("standings");
              }}
            >
              Standings
            </button>
            <button
              className={`px-2 md:px-4 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                matchDetailsTab === "odds"
                  ? "bg-green-600 text-white shadow-[0_0_10px_rgba(34,197,94,0.3)]"
                  : "bg-gray-800/40 text-gray-400 hover:text-white"
              }`}
              onClick={async () => {
                await fetchMatchOdds(id);
                setMatchDetailsTab("odds");
              }}
            >
              Odds
            </button>
          </div>

          {/* Content based on selected tab */}
          {matchDetailsTab === "details" && (
            <div className="space-y-4 md:space-y-6">
              {/* Match Details Card */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 bg-gradient-to-r from-green-900/20 to-emerald-900/10 p-4 md:p-6 rounded-xl border border-green-700/30 shadow-lg">
                <div className="flex-1 flex flex-col items-center">
                  {(m.homeLogo ?? m.home_team_logo) ? (
                    <img
                      src={m.homeLogo ?? m.home_team_logo}
                      alt={m.homeTeam ?? m.home_team}
                      className="w-16 h-16 md:w-24 md:h-24 object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-700 flex items-center justify-center rounded-xl">
                      <span className="text-gray-400 text-xs md:text-sm">
                        N/A
                      </span>
                    </div>
                  )}
                  <h3 className="mt-2 md:mt-4 text-sm md:text-xl font-bold text-white text-center truncate max-w-[120px] md:max-w-full">
                    {m.homeTeam ?? m.home_team ?? "-"}
                  </h3>
                </div>

                {getCenterContent()}

                <div className="flex-1 flex flex-col items-center">
                  {(m.awayLogo ?? m.away_team_logo) ? (
                    <img
                      src={m.awayLogo ?? m.away_team_logo}
                      alt={m.awayTeam ?? m.away_team}
                      className="w-16 h-16 md:w-24 md:h-24 object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-700 flex items-center justify-center rounded-xl">
                      <span className="text-gray-400 text-xs md:text-sm">
                        N/A
                      </span>
                    </div>
                  )}
                  <h3 className="mt-2 md:mt-4 text-sm md:text-xl font-bold text-white text-center truncate max-w-[120px] md:max-w-full">
                    {m.awayTeam ?? m.away_team ?? "-"}
                  </h3>
                </div>
              </div>

              {hasOdds && !isMatchPassed(m) && status.type !== "finished" && (
                <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 p-4 md:p-6 rounded-xl border border-green-700/30 shadow-lg">
                  <div className="flex justify-between items-center mb-3 md:mb-4">
                    <h3 className="text-base md:text-xl font-semibold text-white flex items-center gap-2">
                      <span>🤖</span> AI Analysis
                    </h3>
                  </div>
                  <MatchPredictions
                    match={m}
                    odds={matchOddsData}
                    h2h={matchH2HData}
                    standings={leagueStandings}
                  />
                </div>
              )}
            </div>
          )}

          {matchDetailsTab === "h2h" && (
            <MatchH2H
              matchId={id}
              homeTeamName={m.homeTeam ?? m.home_team}
              awayTeamName={m.awayTeam ?? m.away_team}
            />
          )}

          {matchDetailsTab === "standings" && (
            <MatchStandings leagueId={m.leagueId} />
          )}

          {matchDetailsTab === "odds" && <MatchOdds fixtureId={id} />}
        </div>
      </div>
    </Layout>
  );
}
